import React, {
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react"
import { taskColumns } from "./TaskColumns"
import {
    TaskModel,
    UITaskModel,
    LabelerType,
    getTaskID,
    TaskStatus
} from "../../../../../../../../Models/API/TaskModel"
import { Table } from "resha"
import { CollapsibleSectionHeader } from "../../../../../../../Common/CollapsibleSectionHeader/CollapsibleSectionHeader"
import { Help } from "dash-styled"

export interface TaskListColumnProps {
    appID: string
    isCollaboratorTask: boolean
}

export interface TaskListProps {
    className?: string
    appID: string
    isJobInTerminalState: boolean
    wasTaskListToggled?: boolean
    taskList: UITaskModel[]
    setWasTaskListToggled?: (value: boolean) => void
    onClick: (task: TaskModel) => void
}

export const TaskList: FC<TaskListProps> = ({
    className,
    appID,
    isJobInTerminalState,
    wasTaskListToggled,
    taskList,
    setWasTaskListToggled,
    onClick
}) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [isListVisible, setVisibility] = useState(false)

    const cols = useMemo(
        () =>
            taskColumns({
                appID,
                isCollaboratorTask:
                    taskList !== undefined &&
                    taskList.length > 0 &&
                    taskList[0].labelerType === LabelerType.collaborator
            }),
        [appID, taskList]
    )

    const toggleTaskListVisibility = useCallback(() => {
        setWasTaskListToggled?.(!wasTaskListToggled)
        if (taskList.length > 0) {
            setVisibility(!isListVisible)
        } else {
            setVisibility(false)
        }
    }, [
        isListVisible,
        setWasTaskListToggled,
        taskList.length,
        wasTaskListToggled
    ])

    useEffect(() => {
        if (taskList.length === 0) {
            setVisibility(false)
        } else if (wasTaskListToggled) {
            setVisibility(true)
        }
    }, [setVisibility, taskList, wasTaskListToggled])

    useEffect(() => {
        if (isListVisible) {
            scrollRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "start"
            })
        }
    }, [isListVisible])

    const completedTaskCount = taskList.filter(
        task => task.status === TaskStatus.COMPLETED
    ).length

    return (
        <div data-testid="task-list" ref={scrollRef} className={className}>
            <CollapsibleSectionHeader
                isOpen={isListVisible}
                testId="task-list-header"
                titleTestId="task-list-title"
                separator={false}
                onToggle={
                    taskList.length > 0 ? toggleTaskListVisibility : undefined
                }
            >
                Tasks (
                {taskList.length > 0
                    ? `${completedTaskCount} of ${taskList.length} completed`
                    : 0}
                )
                {!isJobInTerminalState && (
                    <Help
                        className="ml-1 align-text-top"
                        text="Data point is still being processed. More tasks can be generated"
                        tooltipPlacement="right"
                    />
                )}
            </CollapsibleSectionHeader>
            {isListVisible ? (
                <div className="pl-5 pb-4">
                    <div
                        data-testid="task-list-table"
                        className="overflow-x-auto"
                    >
                        <Table<UITaskModel>
                            columns={cols}
                            items={taskList}
                            itemCount={taskList.length}
                            getKey={getTaskID}
                            onRowClick={onClick}
                            className="w-full"
                            checkedItemIds={[]}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    )
}
